import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../api';

const TYPE_COLOR = {
  success: 'var(--teal)',
  error: '#ef4444',
  warning: '#f97316',
  info: 'var(--blue)',
};

const TYPE_ICON = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'i',
};

function timeAgo(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/auth/notifications/');
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await api.patch(`/auth/notifications/${id}/read/`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.post('/auth/notifications/mark-all-read/');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const remove = async (id) => {
    const removed = notifications.find((n) => n.id === id);
    try {
      await api.delete(`/auth/notifications/${id}/`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (removed && !removed.read) setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const displayed = filter === 'unread'
    ? notifications.filter((n) => !n.read)
    : notifications;

  const handleClick = (n) => {
    if (n.data?.message_thread_id) {
      if (!n.read) markRead(n.id);
      navigate(`/messages/${n.data.message_thread_id}`);
    }
  };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: 24 }}>
      <Helmet>
        <title>Notifications — Algovex</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)', flex: 1 }}>
          Notifications
          {unreadCount > 0 && (
            <span style={{
              marginLeft: 10, background: '#ef4444', color: '#fff',
              borderRadius: 10, padding: '2px 9px', fontSize: 11, fontWeight: 700, verticalAlign: 'middle',
            }}>{unreadCount}</span>
          )}
        </h1>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 4 }}>
          {['all', 'unread'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                background: filter === f ? 'var(--teal)' : 'var(--card)',
                color: filter === f ? 'var(--bg)' : 'var(--muted)',
                border: '1px solid ' + (filter === f ? 'var(--teal)' : 'var(--border)'),
                borderRadius: 6, padding: '5px 12px',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
        </div>

        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            style={{
              background: 'none', border: '1px solid var(--border)',
              color: 'var(--teal)', borderRadius: 6,
              padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            Loading…
          </div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
            {filter === 'unread' ? 'No unread notifications.' : 'No notifications yet.'}
          </div>
        ) : (
          displayed.map((n, idx) => {
            const isMessage = !!n.data?.message_thread_id;
            const isClickable = isMessage;
            const isHovered = hoveredId === n.id;
            return (
            <div
              key={n.id}
              onClick={() => handleClick(n)}
              onMouseEnter={() => isClickable && setHoveredId(n.id)}
              onMouseLeave={() => setHoveredId((id) => (id === n.id ? null : id))}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
                padding: '14px 18px',
                borderBottom: idx < displayed.length - 1 ? '1px solid var(--border)' : 'none',
                borderLeft: isMessage ? '3px solid #8b5cf6' : '3px solid transparent',
                background: isHovered && isClickable
                  ? 'rgba(45,212,191,0.08)'
                  : (n.read ? 'transparent' : 'rgba(255,255,255,0.03)'),
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            >
              {/* Type icon */}
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: isMessage ? '#8b5cf6' : (TYPE_COLOR[n.type] || 'var(--blue)'),
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMessage ? 13 : 12,
                fontWeight: 700,
                flexShrink: 0,
                marginTop: 1,
              }}>
                {isMessage ? '💬' : (TYPE_ICON[n.type] || 'i')}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: n.message ? 4 : 0 }}>
                  {isMessage && (
                    <span style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#8b5cf6',
                      border: '1px solid #8b5cf6',
                      borderRadius: 4,
                      padding: '1px 6px',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      flexShrink: 0,
                      marginTop: 2,
                    }}>
                      Message
                    </span>
                  )}
                  <span style={{
                    fontWeight: n.read ? 500 : 700,
                    fontSize: 14,
                    color: 'var(--text)',
                    flex: 1,
                  }}>
                    {n.title}
                  </span>
                  {!n.read && (
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: 'var(--teal)', flexShrink: 0, marginTop: 5,
                    }} />
                  )}
                </div>
                {n.message && (
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
                    {n.message}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: 'var(--muted)', opacity: 0.7 }}>
                    {timeAgo(n.created_at)}
                  </span>
                  {!n.read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--teal)', fontSize: 11, fontWeight: 600, padding: 0,
                      }}
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </div>

              {/* Navigates-somewhere affordance */}
              {isMessage && (
                <span style={{
                  color: isHovered ? 'var(--teal)' : 'var(--muted)',
                  fontSize: 16,
                  flexShrink: 0,
                  alignSelf: 'center',
                  transition: 'color 0.15s',
                }}>
                  ›
                </span>
              )}

              {/* Delete */}
              <button
                onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                title="Dismiss"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--muted)', fontSize: 18, padding: '0 2px',
                  flexShrink: 0, alignSelf: 'flex-start', lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}
